import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import TutorCard from '../components/TutorCard';
import SEO from '../components/SEO';
import { tutorService } from '../services/tutorService';
import { lookupService } from '../services/lookupService';
import { AM_REGIONS as regions, YEREVAN_DISTRICTS as yerevanDistricts } from '../constants/armeniaGeo';
import { ARMENIA_SETTLEMENTS_BY_REGION } from '../constants/armeniaSettlementsByRegion';
import './HomePage.css';

const HomePage = () => {
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  /** Search sent to API — debounced so typing does not disable the input on every key. */
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedModes, setSelectedModes] = useState([]);
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const [priceRange, setPriceRange] = useState('');
  
  // Data states
  const [tutors, setTutors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cityOptions =
    selectedRegion && selectedRegion !== 'Երևան'
      ? ARMENIA_SETTLEMENTS_BY_REGION[selectedRegion] || []
      : [];

  const modes = [
    { value: "Online", label: "Օնլայն" },
    { value: "In-Person", label: "Իր մոտ" },
    { value: "Both", label: "Ձեր մոտ" }
  ];

  // Load filter options on mount + hydrate filters from URL
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const subjectsData = await lookupService.fetchSubjects();
        setSubjects(subjectsData.results || []);
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };

    // Hydrate filter state from current URL query (for shareable links)
    const hydrateFromQuery = () => {
      try {
        const params = new URLSearchParams(window.location.search);

        const urlSearch = params.get('search') || '';
        const urlSubject = params.get('subject') || '';
        const urlRegion = params.get('region') || '';
        const urlCity = params.get('city') || '';
        const urlDistrict = params.get('district') || '';
        const urlModes = params.get('modes') || '';
        const urlPrice = params.get('price') || '';
        const urlPage = parseInt(params.get('page') || '1', 10);

        if (urlSearch) {
          setSearchTerm(urlSearch);
        }
        if (urlSubject) setSelectedSubject(urlSubject);
        if (urlRegion) setSelectedRegion(urlRegion);
        if (urlCity) setSelectedCity(urlCity);
        if (urlDistrict) setSelectedDistrict(urlDistrict);
        if (urlModes) {
          const modeList = urlModes
            .split(',')
            .map((m) => m.trim())
            .filter(Boolean);
          if (modeList.length) {
            setSelectedModes(modeList);
          }
        }
        if (urlPrice) setPriceRange(urlPrice);
        if (!Number.isNaN(urlPage) && urlPage > 1) {
          setCurrentPage(urlPage);
        }
      } catch (e) {
        console.error('Failed to hydrate filters from URL', e);
      }
    };

    loadFilterOptions();
    hydrateFromQuery();
  }, []);

  const SEARCH_DEBOUNCE_MS = 320;

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Fetch tutors when filters change
  useEffect(() => {
    const fetchTutors = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = {
          page: currentPage,
          page_size: 20
        };
        
        if (debouncedSearch) params.search = debouncedSearch;
        if (selectedSubject) params.subject = selectedSubject;
        
        if (selectedRegion === 'Երևան') {
          params.region = 'Երևան';
          params.city = 'Երևան';
          if (selectedDistrict) params.district = selectedDistrict;
        } else if (selectedRegion) {
          params.region = selectedRegion;
          const c = selectedCity.trim();
          if (c) params.city = c;
        }

        if (selectedModes.length > 0) {
          params.modes = selectedModes.join(',');
        }
        
        // Price bands match labels: hourly_rate is stored in AMD on the backend
        if (priceRange === '0-5000') {
          params.max_price = 5000;
        } else if (priceRange === '5000-10000') {
          params.min_price = 5000;
          params.max_price = 10000;
        } else if (priceRange === '10000+') {
          params.min_price = 10000;
        }
        
        const data = await tutorService.fetchTutors(params);
        
        setTutors(data.results || []);
        setTotalCount(data.count || 0);
        setCurrentPage(data.current_page || 1);
        setTotalPages(data.total_pages || 1);
      } catch (err) {
        console.error('Error fetching tutors:', err);
        setError('Չհաջողվեց բեռնել տվյալները: Խնդրում ենք փորձել ավելի ուշ:');
        setTutors([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTutors();
  }, [debouncedSearch, selectedSubject, selectedRegion, selectedCity, selectedDistrict, selectedModes, priceRange, currentPage]);

  // Keep URL query string in sync with current filters (shareable / back-forward friendly)
  useEffect(() => {
    try {
      const params = new URLSearchParams();

      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedSubject) params.set('subject', selectedSubject);

      if (selectedRegion === 'Երևան') {
        params.set('region', 'Երևան');
        params.set('city', 'Երևան');
        if (selectedDistrict) params.set('district', selectedDistrict);
      } else if (selectedRegion) {
        params.set('region', selectedRegion);
        const c = selectedCity.trim();
        if (c) params.set('city', c);
      }

      if (selectedModes.length > 0) {
        params.set('modes', selectedModes.join(','));
      }

      if (priceRange) {
        params.set('price', priceRange);
      }

      if (currentPage && currentPage > 1) {
        params.set('page', String(currentPage));
      }

      const query = params.toString();
      const newRelativeUrl = query
        ? `${window.location.pathname}?${query}`
        : window.location.pathname;

      if (newRelativeUrl !== window.location.pathname + window.location.search) {
        window.history.replaceState(null, '', newRelativeUrl);
      }
    } catch (e) {
      console.error('Failed to sync URL with filters', e);
    }
  }, [
    debouncedSearch,
    selectedSubject,
    selectedRegion,
    selectedCity,
    selectedDistrict,
    selectedModes,
    priceRange,
    currentPage,
  ]);

  // Handle filter changes (reset to page 1)
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const handleModeToggle = (value) => {
    setSelectedModes((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const modeSummaryLabel = () => {
    if (!selectedModes.length) return 'Բոլորը';
    if (selectedModes.length === modes.length) return 'Բոլորը';
    const labelMap = modes.reduce((acc, m) => {
      acc[m.value] = m.label;
      return acc;
    }, {});
    return selectedModes.map((m) => labelMap[m] || m).join(', ');
  };

  // Handle region change - reset city and district
  const handleRegionChange = (e) => {
    const value = e.target.value;
    setSelectedRegion(value);
    setSelectedCity('');
    setSelectedDistrict('');
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1);
  };

  return (
    <div className="homepage">
      <SEO 
        title="Գտեք Ձեզ հարմար մասնագետ Հայաստանում"
        description="Լեզուներ, դպրոցական առարկաներ, ծրագրավորում, երաժշտություն, շախմատ և այլ ոլորտներ։ Ընտրեք ըստ մարզի, քաղաքի կամ Երևանի թաղամասի։"
        url="/"
      />
      
      <div className="hero-section">
        <div className="hero-content">
          <h1>Գտեք Ձեզ հարմար մասնագետ Հայաստանում</h1>
          <p>Լեզուներ, դպրոցական առարկաներ, ծրագրավորում, երաժշտություն, շախմատ և այլ ոլորտներ։<br />Ընտրեք ըստ մարզի, քաղաքի կամ Երևանի թաղամասի։</p>
        </div>
      </div>

      <div className="main-content">
        <div className="search-filters">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Ո՞ր մասնագետն եք փնտրում…"
              value={searchTerm}
              onChange={handleFilterChange(setSearchTerm)}
              autoComplete="off"
            />
          </div>

          <div className="filters">
            <div className="filter-group">
              <label>Առարկա</label>
              <select 
                value={selectedSubject} 
                onChange={handleFilterChange(setSelectedSubject)}
              >
                <option value="">Բոլորը</option>
                {subjects.map(subject => (
                  <option key={subject.id || subject.name} value={subject.name || subject}>
                    {subject.name || subject}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Մարզ</label>
              <select 
                value={selectedRegion} 
                onChange={handleRegionChange}
              >
                <option value="">Ընտրել մարզ</option>
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>

            {selectedRegion && selectedRegion !== 'Երևան' && (
              <div className="filter-group">
                <label>Քաղաք / Գյուղ</label>
                <select
                  value={selectedCity}
                  onChange={handleFilterChange(setSelectedCity)}
                >
                  <option value="">Բոլոր քաղաքներն / գյուղերը</option>
                  {cityOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedRegion === 'Երևան' && (
              <div className="filter-group">
                <label>Թաղամաս</label>
                <select 
                  value={selectedDistrict} 
                  onChange={handleFilterChange(setSelectedDistrict)}
                >
                  <option value="">Բոլոր թաղամասերը</option>
                  {yerevanDistricts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-group">
              <label>Դասի ձևաչափ</label>
              <div
                className="custom-multi-select"
                onClick={() => setModeDropdownOpen((open) => !open)}
              >
                <div className="custom-multi-select-display">
                  <span>{modeSummaryLabel()}</span>
                  <span className="custom-multi-select-arrow">▾</span>
                </div>
                {modeDropdownOpen && (
                  <div
                    className="custom-multi-select-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {modes.map((mode) => (
                      <label
                        key={mode.value}
                        className="custom-multi-select-option"
                      >
                        <input
                          type="checkbox"
                          value={mode.value}
                          checked={selectedModes.includes(mode.value)}
                          onChange={() => handleModeToggle(mode.value)}
                        />
                        <span>{mode.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label>Գինը</label>
              <select 
                value={priceRange} 
                onChange={handleFilterChange(setPriceRange)}
              >
                <option value="">Բոլորը</option>
                <option value="0-5000">0 - 5 000 դրամ</option>
                <option value="5000-10000">5 000 - 10 000 դրամ</option>
                <option value="10000+">10 000+ դրամ</option>
              </select>
            </div>
          </div>

          <button 
            type="button"
            className="search-cta-button"
            onClick={handleSearch}
          >
            <Search size={20} />
            Գտնել մասնագետ
          </button>
        </div>

        <div className="results-section">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>
                Փորձել կրկին
              </button>
            </div>
          )}

          <div className="results-header">
            <h2>
              {loading ? 'Բեռնում...' : `Գտնված մասնագետներ (${totalCount})`}
            </h2>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Բեռնում է...</p>
            </div>
          ) : (
            <>
              <div className="tutors-grid">
                {tutors.map(tutor => (
                  <TutorCard key={tutor.id} tutor={tutor} />
                ))}
              </div>

              {tutors.length === 0 && !error && (
                <div className="no-results">
                  <p>Ոչ մի մասնագետ չի գտնվել Ձեր փնտրման համապատասխան:</p>
                  <p>Փորձեք փոխել ֆիլտրերը:</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Նախորդ
                  </button>
                  
                  <span>
                    Էջ {currentPage} / {totalPages}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Հաջորդ
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* For Specialists Section */}
        <div className="for-specialists-section">
          <h3>Մասնագետ ե՞ք</h3>
          <p>Գրանցվեք և դարձեք հասանելի նոր հաճախորդների համար ամբողջ Հայաստանում։</p>
          <a href="/add-tutor" className="specialists-btn">
            Դառնալ մասնագետ
          </a>
        </div>
      </div>

      {/* Footer Disclaimer */}
      <footer className="footer-disclaimer">
        <p>Հարթակը հանդիսանում է բաց մասնագետների տեղեկատու։</p>
        <p>Կապը և պայմանավորվածությունները կատարվում են անմիջապես մասնագետի հետ։</p>
      </footer>
    </div>
  );
};

export default HomePage;
